/*
 * Copyright © 2023. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect, useState } from "react";

import Typography from "@mui/material/Typography";
import { apiDelete, apiGet } from "../../services/apiClient";
import ButtonLoading from "../Button/ButtonLoading";
import ContentBox from "../Box/ContentBox/ContentBox";
import icon from "../../assets/icons/generic/IconDelete";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import { useNavigate } from "react-router";
import DialogModal from "../DialogModal/DialogModal";
import * as messagesActions from "../../store/messages/actions";

type ServiceWorldCaseDeleteProps = {
  product: AbstractCase;
  caseIsDeletable: boolean;
  setCaseIsDeletable: (isDeletable: boolean) => void;
};

export default function ServiceWorldCaseDelete({ product, caseIsDeletable, setCaseIsDeletable }: ServiceWorldCaseDeleteProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmCaseDeletionModalOpen, setConfirmCaseDeletionModalOpen] = useState(false);
  const navigate = useNavigate();

  async function checkDeletable() {
    const result = await apiGet(`/case_deletion/get_case_deletion_possible_status/${product.id}`);
    setCaseIsDeletable(!result.hasAcquisitionPartnerAdvocard && !result.isAdvancedCase && !result.hasBookingEntry);
    setIsLoading(false);
  }

  useEffect(() => {
    checkDeletable();
  }, []);

  if (isLoading || !caseIsDeletable) {
    return null;
  }

  async function deleteCase() {
    setIsDeleting(true);
    let wasDeleted;

    try {
      await apiDelete(`/case_deletion/force/${product.id}`);
      wasDeleted = true;
    } catch (error) {
      wasDeleted = false;
    }

    messagesActions.addMessage({
      type: wasDeleted ? "success" : "error",
      text: wasDeleted
        ? "Ihr Fall wurde erfolgreich gelöscht"
        : "Ihr Fall konnte nicht gelöscht werden. Dies kann möglicherweise daran liegen, dass bereits Abrechnungen vorliegen.",
    });

    navigate("/servicewelt");
    setIsDeleting(false);
  }

  return (
    <ContentBox icon={icon} headline={"Fall löschen"} alignContent={"stretch"}>
      <Typography sx={{
        marginBottom: 2
      }}>
        Sie benötigen diesen Fall nicht (mehr) und möchten diesen daher löschen? Dann können Sie dies hier tun. <strong>Wichtig:</strong> Dies kann nicht
        rückgängig gemacht werden und alle mit dem Fall verbundenen Daten und Dokumente werden gelöscht!
      </Typography>
      <ButtonLoading isLoading={isDeleting} variant={"contained"} onClick={() => setConfirmCaseDeletionModalOpen(true)} fullWidth={true}>
        Löschen
      </ButtonLoading>
      <DialogModal
        open={confirmCaseDeletionModalOpen}
        onClose={() => setConfirmCaseDeletionModalOpen(false)}
        title={"Fall löschen"}
        actions={
          <ButtonLoading isLoading={isDeleting} variant={"contained"} onClick={deleteCase} fullWidth={true}>
            Löschen
          </ButtonLoading>
        }
      >
        <p>Möchten Sie den Fall wirklich löschen?</p>
      </DialogModal>
    </ContentBox>
  );
}
